import React from 'react';
import { LayoutChangeEvent, View, useWindowDimensions } from 'react-native';
import { Gesture, GestureDetector } from 'react-native-gesture-handler';
import Animated, {
  useAnimatedStyle,
  useSharedValue,
} from 'react-native-reanimated';

type Props = {
  children?: React.ReactNode;
  startWidth?: number;
  minWidth?: number;
  maxWidth?: number;
  hidden?: boolean;
  onLayout: (event: LayoutChangeEvent) => void;
};

export const ResizableSidebar: React.FC<Props> = ({
  children,
  startWidth = 250,
  minWidth = 100,
  maxWidth,
  hidden,
  onLayout,
}) => {
  const { width: ultimateMaxWidth } = useWindowDimensions();
  const finalMaxWidth =
    Math.min(maxWidth || ultimateMaxWidth, ultimateMaxWidth) - 30;
  const width = useSharedValue(
    Math.max(Math.min(startWidth, finalMaxWidth), minWidth),
  );
  const overflow = useSharedValue<'visible' | 'hidden'>('visible');

  const dragPosition = useSharedValue(0);
  const pan = Gesture.Pan()
    .activeOffsetX([-1, 1])
    .onUpdate((event) => {
      const resultingWidth = width.value + event.translationX;
      if (resultingWidth >= minWidth && resultingWidth <= finalMaxWidth) {
        dragPosition.value = event.translationX;
      }
    })
    .onEnd(() => {
      width.value += dragPosition.value;
      dragPosition.value = 0;
    });

  const animatedStyle = useAnimatedStyle(() => ({
    width: width.value,
    overflow: overflow.value,
  }));
  const animatedXStyle = useAnimatedStyle(() => ({
    transform: [{ translateX: dragPosition.value }],
  }));

  const lastVisibleWidth = React.useRef(startWidth);
  React.useEffect(() => {
    if (hidden) {
      lastVisibleWidth.current = width.value;
      width.value = 0;
      overflow.value = 'hidden';
    } else {
      width.value = lastVisibleWidth.current;
      overflow.value = 'visible';
    }
  }, [hidden, overflow, width]);

  return (
    <Animated.View
      style={[
        {
          position: 'relative',
          zIndex: 9999,
          flexDirection: 'row',
          flex: 1,
        },
        animatedStyle,
      ]}
      onLayout={onLayout}
    >
      {children}
      <GestureDetector gesture={pan}>
        <Animated.View
          style={[
            {
              width: 21,
              zIndex: 999,
              position: 'absolute',
              top: 0,
              right: -10,
              bottom: 0,
              alignItems: 'center',
              // react-native-web has grab
              cursor: 'grab' as 'pointer',
            },
            animatedXStyle,
          ]}
        >
          <View
            style={{
              width: 2,
              backgroundColor: '#ddd',
              flex: 1,
              pointerEvents: 'none',
            }}
          />
        </Animated.View>
      </GestureDetector>
    </Animated.View>
  );
};
